// QuestionnairesTemplate.tsx
import {
  Button,
  LogoDark,
  Typography,
  CheckboxOption,
  ShimmerEffect,
} from "@/design-system/Atoms";
import { ProgressStepper } from "@/design-system/Molecules";
import { AppErrorState, AppEmptyState } from "@/design-system/Feedbacks";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuestionnairesService } from "@/rtkQuery/features/hooks";
import { useQuestionnaireSubmission } from "@/rtkQuery/features/hooks/useQuestionnaireSubmission";
import { useNavigate } from "react-router-dom";
import { buildDashboardTabUrl } from "@/hooks/useBuildWithParams";
import { generateZodSchemaFromQuestion, getDefaultValuesForQuestion } from "@/validation-schemas/questionnaires-schemas";
import { ZodObject, type ZodRawShape } from "zod";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";

const QuestionnairesTemplate = () => {
  const navigate = useNavigate();
  const {
    questionnaireData = [],
    questionnaireIsLoading,
    questionnaireError,
    questionnaireRefetch,
  } = useQuestionnairesService();

  const { ref } = useInViewOnce<HTMLDivElement>();

  const { submitQuestionnaire } = useQuestionnaireSubmission();

  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState<any>({});
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [isSubmittingError, setIsSubmittingError] = useState<string>("");
  const [mustStopOption, setMustStopOption] = useState<any>(null);

  const isLastStep = currentStep === questionnaireData.length;
  const currentQuestion = questionnaireData[currentStep - 1];

  const schema = useMemo(() => generateZodSchemaFromQuestion(currentQuestion), [currentQuestion]);
  const defaultValues = useMemo(() => getDefaultValuesForQuestion(currentQuestion), [currentQuestion]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema as ZodObject<ZodRawShape>),
    mode: "onChange",
    defaultValues,
  });

  const selected = watch("selected");
  const email = watch("email");
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    reset(defaultValues);
  }, [currentStep, defaultValues, reset]);

  const handleToggle = (value: string) => {
    setIsSubmittingError("");

    const responseOptions = currentQuestion?.ResponseOptions || [];
    const max = currentQuestion?.MaxSelectedAnswers;

    let updatedSelection: string | string[] = "";

    if (max === 1) {
      updatedSelection = selected === value ? "" : value;
      setValue("selected", updatedSelection, { shouldValidate: true });

      const stopOpt = responseOptions.find(opt => opt.Label === updatedSelection && opt.MustStop);
      setMustStopOption(stopOpt || null);
    } else {
      const selectedArray = Array.isArray(selected) ? [...selected] : [];
      if (selectedArray.includes(value)) {
        updatedSelection = selectedArray.filter((v) => v !== value);
      } else {
        updatedSelection = [...selectedArray, value];
      }
      setValue("selected", updatedSelection, { shouldValidate: true });

      const stopOpt = responseOptions.find(opt => updatedSelection.includes(opt.Label) && opt.MustStop);
      setMustStopOption(stopOpt || null);
    }
  };

  const onSubmit = async (data: any) => {
    setIsSubmittingError("");
    const updatedFormState = { ...formState };
    const selectedValue = data.selected;
    const responseOptions = currentQuestion?.ResponseOptions || [];

    let stopOption = null;

    if (Array.isArray(selectedValue)) {
      stopOption = responseOptions.find(opt => selectedValue.includes(opt.Label) && opt.MustStop);
    } else {
      stopOption = responseOptions.find(opt => opt.Label === selectedValue && opt.MustStop);
    }

    if (stopOption) {
      setMustStopOption(stopOption);
      window.open("https://www.defymedical.com/get-started/", "_blank");
      return;
    }

    if (currentQuestion?.HealthQuestionType === 6) {
      updatedFormState.email = data.email;
      updatedFormState.password = data.password;
      updatedFormState.confirmPassword = data.confirmPassword;
    } else {
      updatedFormState[`step${currentStep}Answer`] = data.selected;
    }

    setFormState(updatedFormState);

    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    } else {
      try {
        setIsSubmittingFinal(true);
        const payload = {
          email: updatedFormState.email || "",
          password: updatedFormState.password || "",
          confirmPassword: updatedFormState.confirmPassword || "",
          isAdultUser: updatedFormState.step1Answer || "",
          reasonForContact: updatedFormState.step2Answer || "",
          interestedMedication: updatedFormState.step3Answer || "",
        };
        const result = await submitQuestionnaire(payload);
        if (!result.success) {
          setIsSubmittingError(result.error)
        } else if (result.success) {
          navigate(buildDashboardTabUrl(DASHBOARD_TABS.UPDATE));
          setFormState({});
          setCurrentStep(1);
        }
      } catch (error) {
        console.error("Submission failed:", error);
      } finally {
        setIsSubmittingFinal(false);
      }
    }
  };

  useEffect(() => {
    setMustStopOption(null);
  }, [currentStep]);

  useEffect(() => {
    if (isSubmittingError) setIsSubmittingError("");
  }, [selected, email, password, confirmPassword]);

  if (!questionnaireIsLoading && questionnaireError) {
    return (
      <div className="max-w-[36vw] mx-auto px-4">
        <div className="rounded-[20px] h-[100vh] flex items-center justify-center">
          <AppErrorState message="Failed to load questionnaire." onRetry={questionnaireRefetch} />
        </div>
      </div>
    );
  }

  if (!questionnaireIsLoading && !questionnaireData.length) {
    return (
      <div className="max-w-[36vw] mx-auto px-4">
        <div className="rounded-[20px] h-[100vh] flex items-center justify-center">
          <AppEmptyState message="No questions found." />
        </div>
      </div>
    );
  }


  return (
    <div className="relative bg-[var(--color-bg-cream)] bg-[url('/images/bg-wizard.svg')] bg-no-repeat bg-cover bg-top overflow-hidden">
      <div className="h-auto pb-[4.68rem] lg:pb-[10rem]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-8/12 w-full mx-auto p-6 rounded-[20px]">
              {questionnaireIsLoading ? (
                <div className="w-full p-6 rounded-2xl mt-10 bg-white/70 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
                  <ShimmerEffect className="h-[80px] w-full mt-10 mb-6" />
                  <ShimmerEffect className="h-[38px] w-[140px] my-6" />
                  <ShimmerEffect className="h-[108px] w-full" />
                  <div className="space-y-4">
                    <ShimmerEffect className="h-[76px] w-full rounded-[8px] mt-10" />
                    <ShimmerEffect className="h-[76px] w-full rounded-[8px]" />
                  </div>
                  <ShimmerEffect className="h-[66px] w-full mt-10" />
                </div>
              ) : (
                <>
                  <ProgressStepper currentStep={currentStep} totalSteps={questionnaireData.length} />
                  <LogoDark className="mb-10" />
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div key={currentStep} className="animate-fade-in-up">
                      <Typography variant="sectionHeading" className="mb-10">
                        {currentQuestion?.Title}
                      </Typography>
                    </div>

                    <div className="space-y-4">
                      {currentQuestion?.HealthQuestionType === 6 ? (
                        ["email", "password", "confirmPassword"].map((field) => (
                          <div key={field} className="mb-3">
                            <input
                              type={field === "email" ? "email" : "password"}
                              id={field}
                              placeholder={field === "confirmPassword" ? "Confirm Password" : field.charAt(0).toUpperCase() + field.slice(1)}
                              className="block w-full px-7 py-5 text-base font-normal leading-6 text-[#181a20] bg-white border border-white rounded focus:border-[#8c8d90] focus:outline-none focus:ring-4 focus:ring-[rgba(24,26,32,0.25)] transition duration-150 ease-in-out"
                              {...register(field)}
                            />
                            {typeof errors[field]?.message === "string" && (
                              <p className="text-red-500 text-sm mt-2">{errors[field]?.message}</p>
                            )}

                          </div>
                        ))

                      ) : (
                        currentQuestion?.ResponseOptions?.map((opt: any) => (
                          <CheckboxOption
                            key={opt.Label}
                            id={`${opt.Label}-${currentStep}`}
                            label={opt.Label}
                            checked={Array.isArray(selected) ? selected.includes(opt.Label) : selected === opt.Label}
                            onChange={() => handleToggle(opt.Label)}
                          />
                        ))
                      )}
                    </div>

                    {typeof errors.selected?.message === "string" && (
                      <p className="text-red-500 text-sm mt-2">{errors.selected.message}</p>
                    )}

                    {isSubmittingError && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm">{isSubmittingError}</p>
                      </div>
                    )}

                    {mustStopOption?.StopText && (
                      <div
                        ref={ref}
                        className="animate-fade-in-up mt-6 p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700"
                        dangerouslySetInnerHTML={{ __html: mustStopOption.StopText }}
                      />
                    )}
                    <Button
                      variant="secondary"
                      size="xl"
                      className="!w-full md:w-auto mt-10"
                      type="submit"
                      disabled={isSubmitting || isSubmittingFinal}
                      loading={isSubmitting || isSubmittingFinal}
                    >
                      {isSubmittingFinal ? "Submitting..." : isLastStep ? "Submit" : "Continue"}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default QuestionnairesTemplate;
